<template>
  <div class="relative">

    <!-- Trigger button -->
    <button
      @click="show = !show"
      class="flex items-center gap-2 px-3.5 py-2 rounded-xl text-[12px] font-semibold
             transition-all duration-200 focus:outline-none
             bg-primary-50 dark:bg-dark-600
             border border-primary-100 dark:border-dark-500
             text-muted dark:text-muted-light
             hover:bg-primary-100 dark:hover:bg-dark-500
             hover:text-dark-700 dark:hover:text-white"
      aria-haspopup="listbox"
      :aria-expanded="show.toString()"
      aria-label="Toggle filter dropdown"
    >
      <!-- Filter icon -->
      <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"/>
      </svg>

      <span>{{ label }}</span>

      <!-- Active count badge -->
      <span
        v-if="selectedValues.length"
        class="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold
               bg-primary-400 text-white"
      >
        {{ selectedValues.length }}
      </span>

      <!-- Chevron -->
      <svg class="w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200"
           :class="show ? 'rotate-180' : ''"
           fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <!-- Dropdown panel -->
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 -translate-y-1 scale-[0.98]"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 -translate-y-1 scale-[0.98]"
    >
      <div
        v-if="show"
        class="absolute top-full mt-2 right-0 z-20 w-56
               bg-surface dark:bg-dark-700
               border border-primary-100 dark:border-dark-600
               rounded-2xl shadow-dropdown overflow-hidden"
        role="listbox"
      >
        <!-- Panel header -->
        <div class="px-4 pt-4 pb-2 border-b border-primary-50 dark:border-dark-600">
          <p class="text-[10px] font-bold uppercase tracking-widest
                    text-muted dark:text-muted-light">
            Filter by {{ field }}
          </p>
        </div>

        <!-- Options -->
        <ul class="p-2 space-y-0.5 max-h-56 overflow-y-auto
                   [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <li
            v-for="(value, index) in availableValues"
            :key="index"
          >
            <label
              :for="`filter_option_${index}`"
              class="flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer
                     transition-colors duration-150 select-none
                     hover:bg-primary-50 dark:hover:bg-dark-600"
            >
              <!-- Custom checkbox -->
              <div class="relative flex-shrink-0">
                <input
                  :id="`filter_option_${index}`"
                  @change="filter"
                  type="checkbox"
                  :value="String(value)"
                  v-model="selectedValues"
                  class="sr-only peer"
                />
                <div
                  class="w-4 h-4 rounded-md border-2 border-primary-200 dark:border-dark-400
                         bg-white dark:bg-dark-600 transition-all duration-150
                         peer-checked:bg-primary-400 peer-checked:border-primary-400
                         flex items-center justify-center"
                >
                  <svg class="w-2.5 h-2.5 text-white opacity-0 peer-checked:opacity-100
                              transition-opacity duration-150 hidden peer-checked:block"
                       fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round"
                          stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                </div>
              </div>

              <!-- Color dot + label -->
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <span class="w-2 h-2 rounded-full flex-shrink-0"
                      :class="getValueColor(value)"/>
                <span class="text-[13px] font-medium text-dark-700 dark:text-white/80 truncate">
                  {{ value }}
                </span>
              </div>
            </label>
          </li>

          <!-- Empty state -->
          <li v-if="!availableValues.length"
              class="px-3 py-4 text-center text-[12px] text-muted dark:text-muted-light">
            No options available
          </li>
        </ul>

        <!-- Clear button -->
        <div v-if="selectedValues.length"
             class="px-3 pb-3 pt-1 border-t border-primary-50 dark:border-dark-600">
          <button
            @click="selectedValues = []; filter()"
            class="w-full text-[12px] font-semibold text-red-400 hover:text-red-500
                   py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30
                   transition-colors duration-150"
          >
            Clear filters
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed } from "vue";

export default {
  name: "DropDownFilter",

  props: {
    datas:        { type: Array,  default: () => [] },
    field:        { type: String, required: true },
    label:        { type: String, default: "Filter" },
    customValues: { type: Array,  default: () => [] },
  },

  emits: ["filter"],

  setup(props, { emit }) {
    const show = ref(false);
    const selectedValues = ref([]);

    const filter = () => {
      emit("filter", selectedValues.value);
    };

    const availableValues = computed(() => {
      if (!props.datas?.length || !props.field) return props.customValues;
      const valuesFromData = props.datas
        .map(data => {
          const key = Object.keys(data).find(k => k.toLowerCase() === props.field.toLowerCase());
          return key ? String(data[key]) : "";
        })
        .filter(val => val !== "");
      return [...new Set([...valuesFromData, ...props.customValues])];
    });

    const getValueColor = (value) => {
      const map = {
        true:     "bg-red-400",
        false:    "bg-primary-400",
        Active:   "bg-primary-400",
        Inactive: "bg-accent-400",
        default:  "bg-muted",
      };
      return map[value] || map.default;
    };

    return { show, selectedValues, availableValues, getValueColor, filter };
  },
};
</script>